import { createSelector } from '@ngrx/store';

import { GuestSelectors, UserSelectors } from '@core/account-data';
import type { HiddenResultPreview } from '@core/interview-results-types';
import { createEntityCollectionStore, DEFAULT_SCOPE_NAME, pipeState } from '@core/ngrx-legacy';
import { JobsWrapperDataService, StageSelectors } from '@core/offers-data';

import type { InterviewResultsReferences } from '../types';

const { actions, selectors, reducer, namespace } =
  createEntityCollectionStore<HiddenResultPreview>()('InterviewResultsHiddenPreview', {
    references: makeRef => [
      makeRef('guestIds', 'guests', GuestSelectors.byIds),
      makeRef('adminIds', 'admins', UserSelectors.byIds),
      makeRef('offerId', 'offer', JobsWrapperDataService.jobItemByIdSelector),
      makeRef('stageId', 'stage', (stageId: number) => StageSelectors.byId(stageId)),
    ],

    customActions: adapter => ({
      fetchCollection: (
        state,
        payload: {
          data: HiddenResultPreview[];
          references: InterviewResultsReferences[];
          scope: string;
          replace?: boolean;
        },
      ) =>
        pipeState(
          state,
          next => (payload.replace ? adapter.resetAll(next) : next),
          next =>
            adapter.markAsPending(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      fetchCollectionSuccess: (
        state,
        payload: {
          data: HiddenResultPreview[];
          references: InterviewResultsReferences[];
          scope: string;
          replace?: boolean;
        },
      ) =>
        pipeState(
          state,
          next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
          next => adapter.setMany(next, payload.data, { scope: payload.scope }),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: payload.data.map(entity => entity.id),
            }),
        ),
    }),
  });

export const candidateEntities = (candidateId: number) =>
  createSelector(selectors.allEntities, entities =>
    entities.filter(entity => entity.candidateId === candidateId),
  );

const InterviewResultsHiddenPreviewSelectors = {
  ...selectors,
  candidateEntities,
};

export {
  actions as InterviewResultsHiddenPreviewActions,
  InterviewResultsHiddenPreviewSelectors,
  reducer as InterviewResultsHiddenPreviewReducer,
  namespace as InterviewResultsHiddenPreviewNamespace,
};
